import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bell, Plane, Clock, AlertTriangle, CalendarIcon, Users } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState('7');

  useEffect(() => {
    fetchNotifications();
  }, [daysAhead]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/notifications/upcoming-flights?days=${daysAhead}`, {
        headers: getAuthHeaders()
      });
      setNotifications(res.data);
    } catch (error) {
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const getDaysLabel = (days) => {
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Mañana';
    return `En ${days} días`;
  };

  const getDaysBadge = (days) => {
    if (days === 0) return 'bg-red-100 text-red-800 border-red-200';
    if (days <= 2) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (days <= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner w-10 h-10"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Bell className="w-8 h-8 text-yellow-500" />
            Vuelos Próximos
          </h1>
          <p className="text-slate-500 mt-1">Pasajeros con vuelos en los próximos días</p>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <Select value={daysAhead} onValueChange={setDaysAhead}>
            <SelectTrigger className="w-40" data-testid="days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 días</SelectItem>
              <SelectItem value="7">7 días</SelectItem>
              <SelectItem value="14">14 días</SelectItem>
              <SelectItem value="30">30 días</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-700">{notifications.filter(n => n.days_until <= 2).length}</p>
              <p className="text-sm text-red-600">Urgentes (0-2 días)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex items-center gap-4">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">{notifications.filter(n => n.days_until > 2 && n.days_until <= 7).length}</p>
              <p className="text-sm text-yellow-600">Esta semana (3-7 días)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <Plane className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{notifications.length}</p>
              <p className="text-sm text-blue-600">Total próximos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            No hay vuelos próximos en los siguientes {daysAhead} días
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((n) => (
            <Card key={n.id} className="hover:shadow-md transition-shadow" data-testid={`notification-${n.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      n.days_until === 0 ? 'bg-red-100' : n.days_until <= 2 ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      <Plane className={`w-6 h-6 ${
                        n.days_until === 0 ? 'text-red-600' : n.days_until <= 2 ? 'text-orange-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{n.passenger_name}</span>
                        <Badge className={getDaysBadge(n.days_until)}>{getDaysLabel(n.days_until)}</Badge>
                        {n.pnr_code && <Badge variant="outline" className="font-mono text-xs">{n.pnr_code}</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Plane className="w-3.5 h-3.5" /> {n.route}
                        </span>
                        {n.airline && <span>• {n.airline}</span>}
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3.5 h-3.5" /> {n.flight_date}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> Gestor: {n.gestor_name}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-slate-900">${n.amount?.toLocaleString('en-US', {minimumFractionDigits: 2})}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
